import { css } from 'lit';

export const analyzeResultStyles = css`
  :host {
    display: block;
    /* Match deep-research-result-block — larger margin so the card
       breathes from the research-log block above; bottom margin
       gives the markdown body room. */
    margin: 24px 0 16px;
    font-family: var(--affine-font-sans-family, system-ui, sans-serif);
  }

  .card {
    border: 1px solid var(--affine-border-color);
    border-radius: 10px;
    background: var(--affine-background-primary-color);
    color: var(--affine-text-primary-color);
    padding: 16px 18px;
    font-size: 13px;
    line-height: 1.5;
  }

  /* Header: 2-row grid so the title + badge stay on the first row
     and the action toolbar wraps below when there's no horizontal
     room. Mirrors deep-research-result-block exactly so the two
     blocks look like siblings. */
  .header {
    display: grid;
    grid-template-columns: 1fr auto;
    column-gap: 16px;
    row-gap: 8px;
    align-items: center;
    padding-bottom: 12px;
    border-bottom: 1px dashed var(--affine-border-color);
    margin-bottom: 12px;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex-wrap: nowrap;
  }

  .title {
    font-weight: 600;
    font-size: 13px;
    color: var(--affine-text-primary-color);
  }

  .kind-badge {
    background: rgba(16, 185, 129, 0.12);
    color: #059669;
    border-radius: 999px;
    padding: 2px 10px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 0 1 auto;
  }

  .title {
    white-space: nowrap;
    flex-shrink: 0;
  }

  .spacer {
    flex: 1;
  }

  .actions {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: flex-end;
    grid-column: 1 / -1;
  }

  @media (min-width: 600px) {
    .actions {
      grid-column: auto;
    }
  }

  /* Buttons mirror agent-stages collapse-toggle / cancel-btn */
  .btn {
    appearance: none;
    background: transparent;
    border: 1px solid var(--affine-border-color);
    color: var(--affine-text-secondary-color);
    padding: 3px 10px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 11px;
    font-family: inherit;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: color 80ms ease, border-color 80ms ease;
  }

  .btn:hover {
    color: var(--affine-primary-color, #1e88e5);
    border-color: var(--affine-primary-color, #1e88e5);
  }

  .btn[data-disabled='true'] {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .btn[data-disabled='true']:hover {
    color: var(--affine-text-secondary-color);
    border-color: var(--affine-border-color);
  }

  /* Metadata as a horizontal pill-row instead of a definition list.
     Reads at a glance and stays compact even when 6 fields are
     present (Dataset · Shape · Kind · Seed · SHA · Style). */
  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 8px;
    margin: 0;
    padding: 0;
  }

  .meta-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    background: var(--affine-hover-color, rgba(0, 0, 0, 0.03));
    border-radius: 6px;
    font-size: 12px;
    color: var(--affine-text-primary-color);
  }

  .meta-item .label {
    color: var(--affine-text-secondary-color);
    font-weight: 500;
  }

  .meta-item .value {
    color: var(--affine-text-primary-color);
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    font-size: 11.5px;
  }

  .toast {
    position: fixed;
    bottom: 24px;
    right: 24px;
    background: var(--affine-text-primary-color);
    color: var(--affine-background-primary-color);
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 12px;
    z-index: 9999;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
  }

  /* HTS plate panel — matches the agent-stages plan panel:
     hover-color background, no nested border, 8px radius. */
  .plate {
    margin-top: 12px;
    padding: 10px 12px;
    background: var(--affine-hover-color, rgba(0, 0, 0, 0.03));
    border-radius: 8px;
  }

  .plate-title {
    font-weight: 600;
    display: flex;
    gap: 6px;
    margin: 0 0 8px;
    font-size: 12px;
    color: var(--affine-text-secondary-color);
  }

  .plate-grid {
    display: grid;
    gap: 2px;
  }

  .plate-corner,
  .plate-col-header,
  .plate-row-header {
    font-size: 10px;
    color: var(--affine-text-secondary-color);
    text-align: center;
    line-height: 16px;
  }

  .well {
    width: 16px;
    height: 16px;
    border-radius: 3px;
    background: var(--affine-tertiary-color, #cbd5e1);
    border: 1px solid transparent;
    cursor: pointer;
    transition: outline 80ms ease, transform 80ms ease;
  }

  .well:hover {
    transform: scale(1.15);
  }

  .well[data-hit='true'] {
    outline: 1.5px solid var(--affine-error-color, #dc2626);
    outline-offset: 1px;
  }

  .well[data-control='true'] {
    border-color: var(--affine-text-secondary-color);
  }

  .well[data-empty='true'] {
    background: transparent;
    border: 1px dashed var(--affine-border-color);
    cursor: default;
  }

  .well[data-empty='true']:hover {
    transform: none;
  }

  .plate-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    font-size: 11px;
    color: var(--affine-text-secondary-color);
    margin-top: 10px;
  }

  .plate-summary strong {
    color: var(--affine-text-primary-color);
    font-weight: 600;
  }

  .plate-legend {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    font-size: 10px;
    color: var(--affine-text-secondary-color);
  }

  .plate-legend-bar {
    flex: 0 0 80px;
    height: 6px;
    border-radius: 3px;
    background: linear-gradient(
      to right,
      hsl(217, 90%, 85%),
      hsl(217, 90%, 65%),
      hsl(217, 90%, 45%),
      hsl(217, 90%, 30%)
    );
  }

  /* CSV preview — first-10-rows table for any non-plate dataset.
     Researchers need to see what they uploaded before asking the
     AI to analyse it. */
  .csv-preview {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .csv-preview-title {
    font-size: 11px;
    color: var(--affine-text-secondary-color);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 600;
  }

  .csv-preview-wrap {
    overflow: auto;
    max-height: 320px;
    border: 1px solid var(--affine-border-color);
    border-radius: 8px;
    background: var(--affine-background-secondary-color);
  }

  .csv-preview-table {
    border-collapse: collapse;
    width: 100%;
    font-size: 11px;
    font-family: var(--affine-font-mono, ui-monospace, monospace);
  }

  .csv-preview-table thead th {
    position: sticky;
    top: 0;
    background: var(--affine-background-primary-color);
    border-bottom: 1px solid var(--affine-border-color);
    padding: 6px 8px;
    text-align: left;
    color: var(--affine-text-primary-color);
    white-space: nowrap;
    font-weight: 600;
    z-index: 1;
  }

  .csv-preview-table tbody td {
    padding: 5px 8px;
    border-bottom: 1px solid
      color-mix(in srgb, var(--affine-border-color) 35%, transparent);
    color: var(--affine-text-primary-color);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 220px;
  }

  .csv-preview-table tbody tr:nth-child(even) td {
    background: color-mix(
      in srgb,
      var(--affine-background-secondary-color) 65%,
      transparent
    );
  }

  .csv-dtype {
    display: inline-block;
    font-size: 9px;
    margin-left: 6px;
    padding: 1px 5px;
    border-radius: 4px;
    font-weight: 500;
    background: color-mix(
      in srgb,
      var(--affine-text-secondary-color) 15%,
      transparent
    );
    color: var(--affine-text-secondary-color);
    text-transform: lowercase;
    letter-spacing: 0;
  }

  .csv-dtype[data-numeric='true'] {
    background: rgba(59, 130, 246, 0.14);
    color: #1d4ed8;
  }

  .csv-dtype[data-bool='true'] {
    background: rgba(168, 85, 247, 0.14);
    color: #7e22ce;
  }

  .csv-dtype[data-date='true'] {
    background: rgba(234, 179, 8, 0.18);
    color: #92400e;
  }

  .csv-null {
    color: var(--affine-text-secondary-color);
    font-style: italic;
  }

  .csv-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .csv-stat-chip {
    font-size: 10.5px;
    background: var(--affine-background-secondary-color);
    border: 1px solid var(--affine-border-color);
    color: var(--affine-text-primary-color);
    padding: 3px 8px;
    border-radius: 999px;
    white-space: nowrap;
  }

  .csv-stat-chip strong {
    font-weight: 600;
  }

  .csv-stat-chip .sep {
    color: var(--affine-text-secondary-color);
    margin: 0 4px;
  }

  .csv-preview-rowhint {
    font-size: 10px;
    color: var(--affine-text-secondary-color);
  }

  /* Plot card — lightweight SVG scatter rendered client-side from
     the preview rows. No charting dependency; we keep the bundle
     lean and the look consistent with the rest of the card. */
  .plots {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
  }

  .plot-card {
    border: 1px solid var(--affine-border-color);
    border-radius: 8px;
    background: var(--affine-background-secondary-color);
    padding: 10px 12px 6px;
  }

  .plot-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
  }

  .plot-title {
    font-size: 12px;
    font-weight: 600;
    color: var(--affine-text-primary-color);
  }

  .plot-actions {
    display: flex;
    gap: 4px;
  }

  .plot-actions button {
    appearance: none;
    background: transparent;
    border: 1px solid var(--affine-border-color);
    color: var(--affine-text-secondary-color);
    padding: 2px 8px;
    border-radius: 5px;
    font-size: 10.5px;
    cursor: pointer;
    font-family: inherit;
  }

  .plot-actions button:hover {
    color: var(--affine-text-primary-color);
    border-color: var(--affine-text-primary-color);
  }

  .plot-svg-wrap {
    width: 100%;
    overflow: hidden;
    border-radius: 4px;
    background: var(--affine-background-primary-color);
  }

  .plot-svg-wrap svg {
    display: block;
    width: 100%;
    height: auto;
    font-family: var(--affine-font-sans-family, system-ui, sans-serif);
  }

  .plot-axis-label {
    font-size: 10px;
    fill: var(--affine-text-secondary-color);
  }

  .plot-tick {
    font-size: 9px;
    fill: var(--affine-text-secondary-color);
  }

  .plot-tick-line {
    stroke: var(--affine-border-color);
    stroke-width: 0.5;
  }

  .plot-axis-line {
    stroke: var(--affine-text-secondary-color);
    stroke-width: 1;
    fill: none;
  }

  .plot-point {
    fill: rgba(59, 130, 246, 0.78);
    stroke: rgba(29, 78, 216, 0.9);
    stroke-width: 0.7;
  }

  .plot-point-control {
    fill: rgba(148, 163, 184, 0.6);
    stroke: rgba(100, 116, 139, 0.9);
  }

  .plot-fit {
    fill: none;
    stroke: rgba(239, 68, 68, 0.9);
    stroke-width: 1.5;
  }

  .plot-empty-note {
    font-size: 11px;
    color: var(--affine-text-secondary-color);
    padding: 8px 0 0;
  }
`;
